import { useCallback, useMemo } from 'react'
import { ListboxGroup } from '@/common'
import useI18n from '@/shared/hooks/usei18n'
import { RequestedProcedures, PreviousProcedures } from '@/config/customsProcedure'
import type { ImportedGood } from '@/features/cbam-reporting/hooks/importedGoods'
import { getBatchRemoveActionsAvailability } from '@/shared/utils/batchUpdateSelectionRules'
import type { BulkUpdateCustomsFormData } from './BulkUpdateCustomsForm.schema'
import { EMPTY_VALUE, isInwardProcessingProcedure } from './BulkUpdateCustomsForm.schema'

type BulkUpdateCustomsFormProps = {
  data: BulkUpdateCustomsFormData
  onChange: (data: BulkUpdateCustomsFormData) => void
  selectedGoods: Pick<ImportedGood, 'requestedCustomsProcedure' | 'previousCustomsProcedure'>[]
}

export default function BulkUpdateCustomsForm({
  data,
  onChange,
  selectedGoods
}: BulkUpdateCustomsFormProps) {
  const { t } = useI18n()
  const removeActionsAvailability = useMemo(
    () => getBatchRemoveActionsAvailability(selectedGoods),
    [selectedGoods]
  )

  const handleFieldChange = useCallback(
    (field: keyof BulkUpdateCustomsFormData) => (value: string) => {
      onChange({ ...data, [field]: value })
    },
    [onChange, data]
  )

  const requestedProcedureOptions = useMemo(
    () => [
      ...(removeActionsAvailability.canRemoveRequestedProcedure
        ? [{ value: EMPTY_VALUE, label: t('cbamReporting:bulkUpdate.removeRequestedProcedure') }]
        : []),
      ...RequestedProcedures.map(proc => ({
        value: proc.code,
        label: t(`common:customsProcedures.${proc.value}`)
      }))
    ],
    [removeActionsAvailability.canRemoveRequestedProcedure, t]
  )

  const previousProcedureOptions = useMemo(
    () => [
      ...(removeActionsAvailability.canRemovePreviousProcedure
        ? [{ value: EMPTY_VALUE, label: t('cbamReporting:bulkUpdate.removePreviousProcedure') }]
        : []),
      ...PreviousProcedures.filter(proc => !isInwardProcessingProcedure(proc.code)).map(proc => ({
        value: proc.code,
        label: t(`common:customsProcedures.${proc.value}`)
      }))
    ],
    [removeActionsAvailability.canRemovePreviousProcedure, t]
  )

  return (
    <div className="space-y-4">
      <div className="grid sm:grid-cols-2 gap-4">
        <ListboxGroup
          label={t('cbamReporting:bulkUpdate.fields.previousProcedure')}
          value={data.previousCustomsProcedure}
          placeholder={t('cbamReporting:bulkUpdate.noChange')}
          options={previousProcedureOptions}
          onChange={handleFieldChange('previousCustomsProcedure')}
        />
        <ListboxGroup
          label={t('cbamReporting:bulkUpdate.fields.requestedProcedure')}
          value={data.requestedCustomsProcedure}
          placeholder={t('cbamReporting:bulkUpdate.noChange')}
          options={requestedProcedureOptions}
          onChange={handleFieldChange('requestedCustomsProcedure')}
        />
      </div>
    </div>
  )
}
